import * as React from "react";
import get from "lodash/get";

import { ComponentBase, ComponentBaseProps } from "../ComponentBase";
import { getExchangeId, getExchanges } from "../../reducers/methods";
import { exchangeRedeem, setScreenName } from "../../actions";
import { WidgetConfig } from "../Widget/Widget";
import { ExchangeRecord } from "../../reducers/exchange/exchanges";

export interface RedeemExchangeProps extends ComponentBaseProps {
  action: string;
  buttonText?: string;
  config: WidgetConfig;
  configName: string;
  exchangeRedeem: typeof exchangeRedeem;
  exchanges: ReturnType<typeof getExchanges>;
  selectedExchangeId: ReturnType<typeof getExchangeId>;
  setScreenName: typeof setScreenName;
  style: React.CSSProperties;
  isLoadingRedeemExchanges: string;
  originalEmail?: string;
}

const successCouponCodeName = "Success Coupon Code";
const successOtherName = "Success GC or Custom";
const successShopifyFreeGiftName = "ShopifyFreeGiftSuccess";
const failureShopifyFreeGiftName = "ShopifyFreeGiftFailure";

interface RedeemButtonProps {
  buttonText?: string;
  config: WidgetConfig;
  exchange?: ExchangeRecord;
  exchangeRedeem: typeof exchangeRedeem;
  selectedExchangeId: string | null;
  originalEmail?: string;
  setScreenName: typeof setScreenName;
  style: React.CSSProperties;
  refCallback: (el: HTMLDivElement | null) => void;
  ariaLabel: string;
  onMouseDown: (event: React.FormEvent<HTMLElement>) => void;
  onDragStart: (event: React.DragEvent<HTMLElement>) => boolean;
  configName: string;
  isEditing: boolean;
  isLoadingRedeemExchanges: string;
}

const getAppropriateSuccessScreenType = (
  exchange?: ExchangeRecord,
  isLoadingRedeemExchanges?: string
) => {
  const codeBank = get(exchange, "reward.delivery") === "codeBank";
  const discount = get(exchange, "reward.rewardType") === "discount";
  const shopifyFreeGift =
    get(exchange, "reward.rewardType") === "shopifyFreeGift";
  if (codeBank && discount) {
    return successCouponCodeName;
  }
  if (shopifyFreeGift && isLoadingRedeemExchanges === "success") {
    return successShopifyFreeGiftName;
  }
  if (shopifyFreeGift && isLoadingRedeemExchanges === "failure") {
    return failureShopifyFreeGiftName;
  }

  return successOtherName;
};

class RedeemButton extends React.Component<RedeemButtonProps> {
  private isMounted = false;

  componentDidMount() {
    this.isMounted = true;
  }

  componentWillUnmount() {
    this.isMounted = false;
  }

  initiateRedeem = (exchangeId: string, originalEmail?: string) => {
    const { exchangeRedeem } = this.props;

    exchangeRedeem({ exchangeId, originalEmail });
  };

  setScreen = () => {
    const { config, exchange, setScreenName, isLoadingRedeemExchanges } =
      this.props;

    const successScreenName = getAppropriateSuccessScreenType(
      exchange,
      isLoadingRedeemExchanges
    );

    const formattedSuccessScreenName = successScreenName
      .replace(/[^a-z0-9]/gi, "")
      .toLowerCase();

    for (const [key, valueObj] of Object.entries(config)) {
      const screenName = get(valueObj, "label", "")
        .replace(/[^a-z0-9]/gi, "")
        .toLowerCase();

      if (screenName === formattedSuccessScreenName) {
        setScreenName(key);
        return;
      }
    }

    console.warn(
      `Widget must be configured with ${successScreenName} screen.`
    );
  };

  handleClick = () => {
    const {
      isEditing,
      exchange,
      selectedExchangeId,
      isLoadingRedeemExchanges,
      originalEmail,
    } = this.props;
    const shopifyFreeGift =
      get(exchange, "reward.rewardType") === "shopifyFreeGift";

    if (isEditing || isLoadingRedeemExchanges === "loading") {
      return;
    }

    const exchangeId = get(exchange, "id");
    if (exchangeId) {
      this.initiateRedeem(exchangeId, originalEmail);
      if (!shopifyFreeGift) {
        this.setScreen();
      }
    } else {
      console.log(
        `Cannot find selected exchange with id ${selectedExchangeId}`
      );
    }
  };

  componentDidUpdate(prevProps: RedeemButtonProps) {
    const shopifyFreeGift =
      get(this.props.exchange, "reward.rewardType") === "shopifyFreeGift";
    // For shopifyFreeGift the success/failure screen is determined by the API
    // result, so navigation is deferred until the response arrives. For all
    // other reward types setScreen() is already called synchronously in
    // handleClick, so we skip it here to avoid a second setScreenName dispatch
    // that can fire after the user has navigated away.
    if (
      this.isMounted &&
      shopifyFreeGift &&
      prevProps.isLoadingRedeemExchanges === "loading" &&
      (this.props.isLoadingRedeemExchanges === "success" ||
        this.props.isLoadingRedeemExchanges === "failure")
    ) {
      this.setScreen();
    }
  }

  render() {
    const {
      configName,
      onDragStart,
      onMouseDown,
      ariaLabel,
      refCallback,
      style,
      buttonText,
      isLoadingRedeemExchanges,
    } = this.props;

    const isLoading = isLoadingRedeemExchanges === "loading";

    return (
      <div
        id={configName}
        onDragStart={onDragStart}
        onMouseDown={onMouseDown}
        aria-label={ariaLabel}
        ref={refCallback}
        style={{
          ...style,
          cursor: isLoading ? "not-allowed" : "pointer",
          opacity: isLoading ? 0.6 : 1,
          pointerEvents: isLoading ? "none" : "auto",
        }}
        onClick={this.handleClick}
      >
        <div>{isLoading ? "Loading..." : buttonText || "REDEEM"}</div>
      </div>
    );
  }
}

export class RedeemExchange extends ComponentBase<RedeemExchangeProps> {
  render() {
    const {
      buttonText,
      config,
      exchangeRedeem,
      exchanges = [],
      selectedExchangeId = "",
      setScreenName,
      style,
      configName,
      isEditing,
    } = this.props;

    const exchange = exchanges.find(
      (exchange) => exchange.id === selectedExchangeId
    );

    return (
      <RedeemButton
        buttonText={buttonText}
        config={config}
        exchange={exchange}
        exchangeRedeem={exchangeRedeem}
        selectedExchangeId={selectedExchangeId}
        originalEmail={this.props.originalEmail}
        setScreenName={setScreenName}
        onDragStart={this.onDragStart}
        onMouseDown={this.onClick}
        style={style}
        refCallback={(component) => {
          this.component = component;
        }}
        ariaLabel={get(this.props, "label", "")}
        configName={configName}
        isEditing={isEditing}
        isLoadingRedeemExchanges={this.props.isLoadingRedeemExchanges}
      />
    );
  }
}
